import { useTranslation } from 'react-i18next';
import { cn } from '../common/utils';
import DropdownSelector from '../components/actions/DropdownSelector';
import * as Icon from '../components/icons';

type SqlHelperType = {
  label: string;
  value: string;
  group: 'import' | 'dataset' | 'analyzed';
  description?: string;
};

const getImportHelpers = (importJobId?: string): SqlHelperType[] => {
  if (!importJobId) return [];

  return [
    {
      label: 'Import Job Preview',
      value: `SELECT * FROM\n{{#import_job_preview[${importJobId}]}}`,
      group: 'import',
      description: 'Preview data from import job',
    },
    {
      label: 'Import Job Input',
      value: `SELECT * FROM\n{{#import_job_input[${importJobId}]}}`,
      group: 'import',
      description: 'Input data from import job',
    },
    {
      label: 'Import Job Errors',
      value: `SELECT * FROM\n{{#import_job_errors[${importJobId}]}}`,
      group: 'import',
      description: 'Error data from import job',
    },
  ];
};

const getDatasetHelpers = (datasetId?: string): SqlHelperType[] => {
  const datasets = [
    'LAYOUT',
    'ASSIGNMENT',
    'ITEM_SET',
    'ORDER_SET',
    'ACTIVITY_FEED',
  ] as const;

  const analyzedDatasets = [
    'ANALYZED_JOB_EVENT',
    'LAYOUT_LOCATION',
    'LAYOUT_FEATURE',
    'LAYOUT_PLANE',
    'OPTIMIZATION_RUN_LOG',
    'PICKING_REQUIREMENT',
    'PICKING_WAVE',
    'PICKLIST_LINE',
    'ACTUALITY_EMPLOYEE_SHIFT',
    'ACTUALITY_EXTENDED_EVENT',
  ] as const;

  const datasetHelpers = datasets
    .map(dataset => [
      {
        label: `${dataset} Table`,
        value: `SELECT * FROM\n{{#${dataset.toLowerCase()}}}`,
        group: 'dataset' as const,
        description: `Access ${dataset} table`,
      },
      {
        label: `${dataset} by ID`,
        value: `SELECT * FROM\n {{#${dataset.toLowerCase()}[${datasetId}]}}`,
        group: 'dataset' as const,
        description: `Access ${dataset} by ID`,
      },
      {
        label: `${dataset} Latest`,
        value: `SELECT * FROM\n {{#${dataset.toLowerCase()}[@latest]}}`,
        group: 'dataset' as const,
        description: `Access latest ${dataset} data`,
      },
      {
        label: `${dataset} All`,
        value: `SELECT * FROM\n {{#${dataset.toLowerCase()}[@all]}}`,
        group: 'dataset' as const,
        description: `Access all ${dataset} data`,
      },
    ])
    .flat();

  const analyzedHelpers = analyzedDatasets.map(dataset => ({
    label: `${dataset} Table`,
    value: `SELECT * FROM\n{{#${dataset.toLowerCase()}}}`,
    group: 'analyzed' as const,
    description: `Access ${dataset} table`,
  }));

  return [...datasetHelpers, ...analyzedHelpers];
};

interface SqlHelperSelectorProps {
  onSelect: (value: string) => void;
  importJobId?: string;
  className?: string;
}

const SqlHelperSelector: React.FC<SqlHelperSelectorProps> = ({
  onSelect,
  importJobId,
  className,
}) => {
  const { t } = useTranslation('app');

  // Always include dataset/analyzed helpers, and only add import helpers if importJobId is a non-empty string
  const importHelpers = importJobId ? getImportHelpers(importJobId) : [];
  const datasetHelpers = getDatasetHelpers();
  const allHelpers = [...importHelpers, ...datasetHelpers];

  // Debug logs
  // console.log('SqlHelperSelector importJobId:', importJobId);
  // console.log('SqlHelperSelector allHelpers:', allHelpers);

  const renderHelper = (helper: SqlHelperType | undefined) => {
    if (!helper) return null;
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="text-sm">{helper.label}</div>
          {helper.description && (
            <div className="text-menu/70 text-xs">{helper.description}</div>
          )}
        </div>
        <Icon.Copy className="h-4 w-4 fill-current opacity-50" />
      </div>
    );
  };

  return (
    <DropdownSelector
      className={cn('w-full', className)}
      label={t('SQL Helpers', { datasetId: importJobId })}
      value={t`Select Helper`}
      values={allHelpers.map(h => h.value)}
      renderValue={(value: string) => {
        const helper = allHelpers.find(h => h.value === value);
        return helper?.label || value;
      }}
      onChange={onSelect}
      panelMode
      //   panelModeMultiline
      DropAlignRight
      widthFull
      valuePrimary
      //   multiline
      light
      hasSearch
      hasSearchLabel={t`Search Helpers`}
      renderValueSelected={(value: string) => {
        const helper = allHelpers.find(h => h.value === value);
        return renderHelper(helper);
      }}
      filterValue={(value: string, searchString: string) => {
        const helper = allHelpers.find(h => h.value === value);
        if (!helper) return false;
        const searchLower = searchString.toLowerCase();
        return (
          helper.label.toLowerCase().includes(searchLower) ||
          helper.description?.toLowerCase().includes(searchLower) ||
          false
        );
      }}
    />
  );
};

export { getImportHelpers };

export default SqlHelperSelector;
